import { LinesEditorComponent } from './../../../../components/lines-editor/lines-editor.component';
import { Component, inject, OnDestroy, OnInit, signal, ViewChild, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LocalizationModule, LocalizationService, SubscriptionService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  ICellRendererParams,
  RowSelectedEvent,
} from '@ag-grid-community/core';
import { AgGridAngular } from '@ag-grid-community/angular';
import { AgGridComponent, FieldTypes, MathCellEditorComponent } from '@digiwall/ag-grid';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import {
  CreateSupplierDeliveryNoteFromPurchaseOrderDto,
  CreateSupplierDeliveryNoteLineDto,
  DocLineRole,
  LabelPrintContextConfigDto,
  SupplierDeliveryNoteService,
} from '@digiwall/goliath-documents/proxy';
import { CurrentCompanyStore, GroupRowCellRendererComponent } from '@digiwall/goliath-core';
import { ColumnDefinitionsService } from '../../../../services/column-definitions.service';
import { LabelSubContext, StorageLocationLookupDto } from '@digiwall/goliath-core/proxy';
import {
  ArticleLotDto,
  ArticleProductDimension,
  ArticleProductRole,
  ArticleRangeValueDto,
  ArticleSerialDto,
  ArticleService,
} from '@digiwall/goliath-products/proxy';
import { LabelPrinterHelper, PrintLabelSubContextEntityDto } from '@digiwall/goliath-products';
import { concatMap, from, Observable, of, Subject, takeUntil, tap } from 'rxjs';
import { LoadingService, NewGuid } from '@digiwall/core';
import { rateFormatter } from '../../../../components/formatters';

const PRINT_LABEL_STORAGE_KEY = 'supplier-receipt/print-label';

@Component({
  selector: 'document-supplier-receipt-from-purchase-order',
  templateUrl: './supplier-receipt-from-purchase-order.component.html',
  styleUrl: './supplier-receipt-from-purchase-order.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    LocalizationModule,
    DialogBoxComponent,
    LinesEditorComponent,
  ],
})
export class SupplierReceiptFromPurchaseOrderComponent implements OnInit, OnDestroy {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  constructor(private subscriptionService: SubscriptionService) {}

  protected purchaseOrderId!: string;

  public setup(purchaseOrderId: string): void {
    this.purchaseOrderId = purchaseOrderId;
  }

  private readonly offcanvasService = inject(NgbActiveOffcanvas);
  private readonly deliveryNoteService = inject(SupplierDeliveryNoteService);
  private readonly localizationService = inject(LocalizationService);
  private readonly toasterService = inject(ToasterService);
  private readonly currentCompanyStore = inject(CurrentCompanyStore);
  private readonly columnDefinitionsService = inject(ColumnDefinitionsService);
  private readonly loadingService = inject(LoadingService);
  private readonly articleService = inject(ArticleService);
  private readonly labelPrinterHelper = inject(LabelPrinterHelper);

  /** User option — print labels for the received lines after the receipt is validated. Defaults to the user's last choice. */
  protected printLabel = window.localStorage.getItem(PRINT_LABEL_STORAGE_KEY) === 'true';

  set lines(value: any[]) {
    this._lines = value?.filter(x => !x.isDeposit);
    this.processLines();
  }

  protected chapterLineRole = DocLineRole.DocChapter;

  getContextMenuItems = _ => [];

  _lines: CreateSupplierDeliveryNoteLineDto[];
  public get lines() {
    return this._lines;
  }
  protected selectedLines: CreateSupplierDeliveryNoteLineDto[] = [];
  protected readonly isSaving = signal(false);
  protected isLoading = true;

  private readonly agGrid = viewChild<AgGridComponent>('agGrid');
  private readonly updateGridTrigger = signal<number>(0);

  protected dialogBoxTitle = '';
  protected readonly gridOptions: GridOptions = {
    stopEditingWhenCellsLoseFocus: true,
  };

  private destroy$ = new Subject<void>();
  private totalRow = {
    id: NewGuid,
    bottomRow: true,
    description: this.localizationService.instant('Doc::Total'),
    selectedQuantityToDeliver: 0,
    totalWeight: 0,
    totalPrice: 0,
    isBottom: true,
  };

  protected pinnedBottomRowData: any[] = [this.totalRow];

  private processLines(): void {
    if (!this._lines?.length) return;
    from(this._lines)
      .pipe(
        concatMap(line => this.onLineTotalRecalculationRequired(line)),
        tap(() => this.lineGrid.grid.grid.api.autoSizeAllColumns()),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.lineGrid.grid.grid.api.autoSizeAllColumns();
  }

  private isCellEditable = (
    params: EditableCallbackParams<CreateSupplierDeliveryNoteLineDto, any>,
  ) => params.data?.lineRole == DocLineRole.SupplierPurchaseOrderArticle;

  ngOnInit(): void {
    this.dialogBoxTitle = this.localizationService.instant(
      'Doc::SupplierReceipt:GenerateFromPurchaseOrder',
    );
    // this.initColumnDefs();
    this.loadLines();
  }

  protected columnDefs: ColDef[] = [
    {
      showRowGroup: 'lineFromId',
      cellRenderer: GroupRowCellRendererComponent,
      suppressHeaderMenuButton: true,
      pinned: 'left',
      suppressMovable: true,
      width: 40,
      resizable: false,
      suppressAutoSize: true,
    },
    {
      field: 'displayReference',
      headerName: '#',
      type: FieldTypes.String,
      width: 70,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      sort: 'asc',
      pinned: 'left',
      suppressMovable: true,
      suppressAutoSize: true,
    },
    {
      field: '_metaType',
      headerName: '',
      width: 120,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      suppressAutoSize: true,
      resizable: false,
      cellRenderer: (
        params: ICellRendererParams<CreateSupplierDeliveryNoteLineDto & { bottomRow?: boolean }>,
      ) => {
        if (params.data?.bottomRow) return '';
        let stockStr = '';
        let typeStr = '';
        if (params.data?.lineFromId == null && params.data?.isStockManaged === true) {
          // Show 'is stock managed' article line if no parent, or chapter line, or in other words only on parent lines
          stockStr = `
            <span class="badge rounded-pill" style="color:var(--bs-success); background-color: rgba(var(--bs-success-rgb), 0.1);" title="${this.localizationService.instant('Product::Article:StockManaged')}">
              <i class="digi-out-stock-inventories"></i>
            </span>`;
        }
        return stockStr + typeStr;
      },
      colSpan: params => {
        if (!params.data?.bottomRow) return 1;
        const cols = params.api.getAllDisplayedColumns();
        const from = cols.findIndex(c => c.getColId() === '_metaType');
        const to = cols.findIndex(c => c.getColId() === 'totalWeight');
        return from !== -1 && to !== -1 ? Math.max(1, to - from) : 1;
      },
    },
    {
      field: 'code',
      headerName: this.localizationService.instant('Doc::CustomerReference'),
    },
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
    },
    {
      field: 'articlePurchaseUnitCode',
      headerName: this.localizationService.instant('Product::Article:ProductPurchaseUnit'),
    },
    {
      field: 'unitPrice',
      headerName: this.localizationService.instant('::UnitPrice'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.priceDecimals,
      },
      filter: false,
      editable: params => this.isCellEditable(params),
      cellEditor: MathCellEditorComponent,
    },
    {
      field: 'discount',
      headerName: this.localizationService.instant('Product::Article:Discount'),
      type: FieldTypes.Percentage,
      cellRendererParams: {
        decimals: 2,
      },
      filter: false,
      editable: params => this.isCellEditable(params),
      cellEditor: 'agNumberCellEditor',
      valueFormatter: rateFormatter(),
    },
    {
      field: 'orderedQuantity',
      headerName: this.localizationService.instant('Doc::SupplierDeliveryNote:OrderedQuantity'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.quantityDecimals,
      },
      filter: false,
    },
    {
      field: 'remainingQuantityToReceive',
      headerName: this.localizationService.instant(
        'Doc::SupplierDeliveryNote:RemainingQuantityToReceive',
      ),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.quantityDecimals,
      },
      filter: false,
    },
    {
      field: 'selectedQuantityToReceive',
      headerName: this.localizationService.instant('Doc::SupplierDeliveryNote:ReceivedQuantity'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.quantityDecimals,
      },
      filter: false,
      editable: params =>
        params.data.lineRole == DocLineRole.SupplierPurchaseOrderArticle &&
        params.node.isSelected() &&
        params.data.remainingQuantityToReceive > 0 &&
        !params.data.articleUsesSerial,
    },
    this.columnDefinitionsService.getStockLocationColDef({
      isCellEditable: this.isCellEditable,
      setLineStockageLocation: (
        lineId: string,
        stockLocationId: string,
        storage: StorageLocationLookupDto,
      ) => {
        const line = this.lines.find(x => x.id == lineId);
        if (line) {
          line.stockLocationId = stockLocationId;
          line.stockLocation = storage.code;
        }
      },
      getDocument: () => null,
      withStockOnly: (item: CreateSupplierDeliveryNoteLineDto) =>
        item.selectedQuantityToReceive > 0,
      withQuantity: null,
    }),
    this.columnDefinitionsService.getArticleLotColDef({
      isCellEditable: this.isCellEditable,
      setArticleLot: (lineId: string, articleLotId: string, articleLot: ArticleLotDto) => {
        const line = this.lines.find(x => x.id == lineId);
        if (line) {
          line.articleLotId = articleLotId;
          line.articleLot = articleLot.code;
        }
      },
      articleLotFormatter: this.articleLotFormatter.bind(this),
    }),
    this.columnDefinitionsService.getArticleRange1ColDef(),
    this.columnDefinitionsService.getArticleRangeValue1ColDef({
      isCellEditable: this.isCellEditable,
      setArticleRangeValue1: (lineId: string, valueId: string, value: ArticleRangeValueDto) => {
        const line = this.lines.find(x => x.id == lineId);
        if (line) {
          line.articleRangeValue1Id = valueId;
          line.articleRangeValue1 = value.name.current;
        }
      },
    }),
    this.columnDefinitionsService.getArticleRange2ColDef(),
    this.columnDefinitionsService.getArticleRangeValue2ColDef({
      isCellEditable: this.isCellEditable,
      setArticleRangeValue2: (lineId: string, valueId: string, value: ArticleRangeValueDto) => {
        const line = this.lines.find(x => x.id == lineId);
        if (line) {
          line.articleRangeValue2Id = valueId;
          line.articleRangeValue2 = value.name.current;
        }
      },
    }),
    // Article serial is editable is manual serials are used
    {
      field: 'articleSerialNumber',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleSerial'),
      editable: params =>
        params.data.articleUsesSerial &&
        !params.data.articleUsesAutoSerial &&
        this.isCellEditable(params),
      type: FieldTypes.String,
      filter: false,
      hide: true,
    },
    {
      field: 'length',
      headerName: this.localizationService.instant('Product::Stock:L'),
      type: FieldTypes.Number,
      width: 80,
      hide: true,
      editable: params =>
        params.data.articleUsesSerial &&
        (params.data.dimension == ArticleProductDimension.Length ||
          params.data.dimension == ArticleProductDimension.Volume ||
          params.data.dimension == ArticleProductDimension.Surface) &&
        this.isCellEditable(params),
      filter: false,
    },
    {
      field: 'width',
      headerName: this.localizationService.instant('Product::Stock:W'),
      type: FieldTypes.Number,
      width: 80,
      hide: true,
      editable: params =>
        params.data.articleUsesSerial &&
        (params.data.dimension == ArticleProductDimension.Volume ||
          params.data.dimension == ArticleProductDimension.Surface) &&
        this.isCellEditable(params),
      filter: false,
    },
    {
      field: 'thickness',
      headerName: this.localizationService.instant('Product::Stock:T'),
      type: FieldTypes.Number,
      width: 80,
      hide: true,
      editable: params =>
        params.data.articleUsesSerial &&
        params.data.dimension == ArticleProductDimension.Volume &&
        this.isCellEditable(params),
      filter: false,
    },
    {
      field: 'totalWeight',
      headerName: this.localizationService.instant('::TotalWeight'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.weightDecimals,
      },
      filter: false,
    },
    {
      field: 'totalPrice',
      headerName: this.localizationService.instant('::TotalPrice'),
      type: FieldTypes.Currency,
      filter: false,
    },
  ];

  private loadLines(): void {
    this.isLoading = true;
    this.deliveryNoteService.getLinesForReception(this.purchaseOrderId).subscribeOnce(
      lines => {
        this.lines = [...lines];
        this.isLoading = false;
        this.updateGridTrigger.set(this.updateGridTrigger() + 1);
        requestAnimationFrame(() => {
          this.preselectAllRows();
          this.updateExtendedColumnVisibility();
        });
      },
      () => {
        this.isLoading = false;
      },
    );
  }

  protected onCellValueChanged = (event: CellValueChangedEvent<any, any>) => {
    const colId = event.column.getColId();

    if (colId === 'selectedQuantityToReceive') {
      if (
        event.data.selectedQuantityToDeliver > event.data.remainingQuantityToDeliver &&
        !event.data.allowOrderedQuantityOverrun
      ) {
        this.toasterService.error('Doc::DeliveryNote:ExceededQuantityForReceipt');
        event.data.selectedQuantityToDeliver = event.data.remainingQuantityToDeliver;
      }
      if (event.data.selectedQuantityToDeliver == 0) event.node.setSelected(false);
    }

    if (colId === 'selectedQuantityToReceive' || colId === 'unitPrice' || colId === 'discount') {
      this.onLineTotalRecalculationRequired(event.data).subscribeOnce(
        () => {},
        () => {
          event.data[colId] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({ update: [event.data] });
        },
      );
      this.lineGrid.grid.grid.api.applyTransaction({ update: [event.data] });
    }
  };

  private updateExtendedColumnVisibility(): void {
    // Base visibility on what a line *needs* (so empty-but-required cells still show for assignment),
    // and match the real column field names produced by ColumnDefinitionsService (…Id variants).
    const hasLot = this.lines.some(l => l.articleProductRoleId === ArticleProductRole.Lot);
    const hasRange1 = this.lines.some(l => !!l.articleRange1Id);
    const hasRange2 = this.lines.some(l => !!l.articleRange2Id);
    const hasSerial = this.lines.some(l => l.articleUsesSerial);
    const hasDimension = this.lines.some(l => !!l.dimension);

    this.columnDefs = this.columnDefs.map(col => {
      if (col.field === 'articleLotId') return { ...col, hide: !hasLot };
      if (col.field === 'articleRange1') return { ...col, hide: !hasRange1 };
      if (col.field === 'articleRangeValue1Id') return { ...col, hide: !hasRange1 };
      if (col.field === 'articleRange2') return { ...col, hide: !hasRange2 };
      if (col.field === 'articleRangeValue2Id') return { ...col, hide: !hasRange2 };
      if (col.field === 'articleSerialNumber') return { ...col, hide: !hasSerial };
      if (col.field === 'length') return { ...col, hide: !hasDimension };
      if (col.field === 'width') return { ...col, hide: !hasDimension };
      if (col.field === 'thickness') return { ...col, hide: !hasDimension };
      return col;
    });
  }

  private preselectAllRows(): void {
    this.lineGrid?.grid?.grid?.api?.forEachNode(node => {
      if (
        node.data?.lineRole === DocLineRole.SupplierPurchaseOrderArticle &&
        node.data?.remainingQuantityToReceive > 0
      ) {
        node.setSelected(true);
      }
    });
  }

  private onLineTotalRecalculationRequired(
    line: CreateSupplierDeliveryNoteLineDto & { totalWeight?: number; totalPrice?: number },
  ): Observable<any> {
    if (line.lineRole !== DocLineRole.SupplierPurchaseOrderArticle) {
      return of(null); // Skip non-applicable lines
    }

    return this.deliveryNoteService
      .getSupplierDeliveryNoteLineTotal({
        purchaseOrderId: this.purchaseOrderId,
        lineId: line.sourceLineId,
        quantity: line.selectedQuantityToReceive,
        unitPrice: line.unitPrice,
        discount: line.discount,
      })
      .pipe(
        tap(({ totalWeight, totalPrice }) => {
          line.totalWeight = totalWeight;
          line.totalPrice = totalPrice;

          this.lineGrid.grid.grid.api.applyTransaction({ update: [line] });
          this.computeTotalRow();
        }),
      );
  }

  private computeTotalRow() {
    const selectedIds = new Set(this.selectedLines.map(l => l.id));
    const articleLines = this.lines.filter(
      line => line.lineRole == DocLineRole.SupplierPurchaseOrderArticle && selectedIds.has(line.id),
    );

    this.totalRow.selectedQuantityToDeliver = articleLines.reduce(
      (total, line) => (total += line.selectedQuantityToReceive),
      0,
    );
    this.totalRow.totalWeight = articleLines.reduce(
      (total, line) => (total += line.totalWeight),
      0,
    );
    this.totalRow.totalPrice = articleLines.reduce((total, line) => (total += line.totalPrice), 0);

    let bottomRowNode = this.lineGrid.grid.grid.api.getPinnedBottomRow(0);

    this.lineGrid.grid.grid.api.redrawRows({ rowNodes: [bottomRowNode] });
  }

  protected onRowSelected = (
    grid: AgGridAngular,
    _event: RowSelectedEvent<CreateSupplierDeliveryNoteLineDto>,
  ) => {
    this.selectedLines = grid.api.getSelectedRows();
    grid.api.redrawRows();
    this.computeTotalRow();
  };

  protected canSave(): boolean {
    if (this.isSaving() || this.isLoading || this.selectedLines.length === 0) return false;

    for (const line of this.selectedLines) {
      if (!line.stockLocationId) return false;
      const qty = line.selectedQuantityToReceive ?? 0;
      if (qty <= 0 || qty > line.remainingQuantityToReceive) return false;
    }

    return true;
  }

  protected onPrintLabelChange(value: boolean): void {
    this.printLabel = value;
    window.localStorage.setItem(PRINT_LABEL_STORAGE_KEY, String(value));
  }

  protected save(): void {
    if (!this.canSave()) return;

    // No label printing → create straight away.
    if (!this.printLabel) {
      this.createReceipt(this.buildBaseDto(false));
      return;
    }

    // Collect the label configuration (template + printer per article type) in the same popup used by
    // the document line-print flow, then send it along with the create call. The backend dispatches
    // the print jobs once the receipt is created and validated.
    const contexts = this.buildLabelPrintContexts();
    if (contexts.length === 0) {
      this.createReceipt(this.buildBaseDto(false));
      return;
    }

    this.labelPrinterHelper
      .selectMultipleTemplateConfig(this.articleService, contexts)
      .subscribeOnce(result => {
        if (result == null) return; // popup cancelled → stay in the dialog

        const labelPrintConfigs: LabelPrintContextConfigDto[] = result.selections
          .filter(s => s.layoutId && s.printerId && s.contextIndex < contexts.length)
          .map(s => ({
            subContext: contexts[s.contextIndex].subContext,
            labelLayoutId: s.layoutId,
            printerId: s.printerId,
          }));

        this.createReceipt({
          ...this.buildBaseDto(true),
          printByQuantity: result.printByQuantity,
          labelPrintConfigs,
        });
      });
  }

  private buildBaseDto(printLabel: boolean): CreateSupplierDeliveryNoteFromPurchaseOrderDto {
    return {
      linesForDelivery: this.selectedLines,
      printLabel,
      printByQuantity: false,
      labelPrintConfigs: [],
    };
  }

  /** Groups the selected article lines into the print contexts (serialized / non-serialized). */
  private buildLabelPrintContexts(): PrintLabelSubContextEntityDto[] {
    const articleLines = this.selectedLines.filter(
      line => line.lineRole === DocLineRole.SupplierPurchaseOrderArticle && !!line.articleId,
    );

    const toPrintEntities = (lines: CreateSupplierDeliveryNoteLineDto[]) =>
      lines.map(line => ({
        entityId: line.id,
        articleId: line.articleId!, // guaranteed by the !!line.articleId filter above
        quantity: line.selectedQuantityToReceive ?? 1,
      }));

    const contexts: PrintLabelSubContextEntityDto[] = [];

    const serialLines = articleLines.filter(line => line.articleUsesSerial);
    if (serialLines.length > 0) {
      contexts.push({
        printEntities: toPrintEntities(serialLines),
        subContext: LabelSubContext.ArticleProductSerial,
        label: 'Product::ArticleProductSerial',
      });
    }

    const nonSerialLines = articleLines.filter(line => !line.articleUsesSerial);
    if (nonSerialLines.length > 0) {
      contexts.push({
        printEntities: toPrintEntities(nonSerialLines),
        subContext: LabelSubContext.ArticleProductInfo,
        label: 'Product::ArticleProductInfo',
      });
    }

    return contexts;
  }

  private createReceipt(dto: CreateSupplierDeliveryNoteFromPurchaseOrderDto): void {
    this.isSaving.set(true);
    this.loadingService.interceptNextRequest();
    this.deliveryNoteService
      .createFromSupplierPurchaseOrder(this.purchaseOrderId, dto)
      .subscribeOnce(
        result => {
          this.isSaving.set(false);
          this.toasterService.success(
            this.localizationService.instant(
              'Doc::SupplierReceipt:GenerationSuccess',
              result.displayName,
            ),
          );
          this.offcanvasService.close(result.id);
        },
        () => {
          this.isSaving.set(false);
        },
      );
  }

  private articleSerialFormatter(data: any, articleSerial: any): string {
    if (data == null || articleSerial == null) return '';
    if (typeof articleSerial === 'string')
      return articleSerial != 'articleSerial' ? articleSerial : '';
    return articleSerial.serialNumber ?? '';
  }

  private articleLotFormatter(data: any, articleLot: any): string {
    if (data == null || articleLot == null) return '';
    if (typeof articleLot === 'string') return articleLot != 'articleLot' ? articleLot : '';
    return articleLot.code ?? '';
  }

  protected rowClassRules = {
    'footer-row': params => params.data?.bottomRow,
    'ag-row-disabled': params => params.data?.remainingQuantityToReceive == 0,
    'disabled-row': (params: { node: { isSelected: () => boolean } }) => !params.node.isSelected(),
  };

  getDataPath = (line, paths: string[] = []) => {
    if (line == null) return paths;

    if (line.lineFromId != null) {
      paths = this.getDataPath(
        this.lines.find(x => x.id == line.lineFromId),
        paths,
      );
    }
    paths.push(line.id);
    return paths;
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
